import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import BetStartButton from '../../buttons/betStartButton';
import RoundFinishButton from '../../buttons/roundFinishButton';
import EventCheckOutButton from '../../buttons/eventCheckOutButton';
import StartReserveButton from '../../buttons/startReserveButton';

const pad2 = (value) => String(value).padStart(2, '0');

const unique = (list) => Array.from(new Set(list));

function FixedOrSelect({ value, options, onChange, minWidth = 80, optionSuffix = '' }) {
  if (options.length === 1) {
    return <Typography sx={{ minWidth }}>{`${options[0]}${optionSuffix}`}</Typography>;
  }

  return (
    <FormControl size="small" sx={{ minWidth }}>
      <Select value={value} onChange={onChange}>
        {options.map((option) => (
          <MenuItem key={`${optionSuffix}-${option}`} value={option}>{`${option}${optionSuffix}`}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function OwnerRoundControls(props) {
  const [errorMessage, setErrorMessage] = useState('');
  const [startButtonsLocked, setStartButtonsLocked] = useState(false);
  const isCompletedEvent = Boolean((props.event?.completion_status && props.event.completion_status !== 'active') || props.disabled);

  const dateWindow = useMemo(() => {
    const now = new Date();
    const min = new Date(now.getTime() + 60 * 1000);
    const maxExclusive = new Date(now.getTime() + 72 * 60 * 60 * 1000);

    const days = [];
    const cursor = new Date(min.getFullYear(), min.getMonth(), min.getDate());
    const last = new Date(maxExclusive.getFullYear(), maxExclusive.getMonth(), maxExclusive.getDate());
    while (cursor <= last) {
      days.push({
        year: cursor.getFullYear(),
        month: cursor.getMonth() + 1,
        day: cursor.getDate()
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return { min, maxExclusive, days };
  }, [props.round?.id, props.round?.number]);

  const yearOptions = useMemo(() => unique(dateWindow.days.map((d) => d.year)), [dateWindow.days]);
  const [selectedYear, setSelectedYear] = useState(yearOptions[0]);

  const monthOptions = useMemo(
    () => unique(dateWindow.days.filter((d) => d.year === selectedYear).map((d) => d.month)),
    [dateWindow.days, selectedYear]
  );
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);

  const dayOptions = useMemo(
    () => unique(dateWindow.days.filter((d) => d.year === selectedYear && d.month === selectedMonth).map((d) => d.day)),
    [dateWindow.days, selectedYear, selectedMonth]
  );
  const [selectedDay, setSelectedDay] = useState(dayOptions[0]);

  const hourOptions = useMemo(() => Array.from({ length: 24 }, (_, idx) => idx), []);
  const minOptions = useMemo(() => Array.from({ length: 60 }, (_, idx) => idx), []);
  const [selectedHour, setSelectedHour] = useState(dateWindow.min.getHours());
  const [selectedMinute, setSelectedMinute] = useState(dateWindow.min.getMinutes());

  useEffect(() => {
    setSelectedYear(yearOptions[0]);
    setStartButtonsLocked(false);
    setErrorMessage('');
  }, [yearOptions]);

  useEffect(() => {
    if (!monthOptions.includes(selectedMonth)) {
      setSelectedMonth(monthOptions[0]);
    }
  }, [monthOptions, selectedMonth]);

  useEffect(() => {
    if (!dayOptions.includes(selectedDay)) {
      setSelectedDay(dayOptions[0]);
    }
  }, [dayOptions, selectedDay]);

  const baseDisabled =
    isCompletedEvent ||
    props.round.answers == '[]' ||
    (props.round.starttime !== null && props.round.starttime !== '');

  const sharedDisabled = baseDisabled || startButtonsLocked;

  const scheduledStartTime = `${selectedYear}-${pad2(selectedMonth)}-${pad2(selectedDay)} ${pad2(selectedHour)}:${pad2(selectedMinute)}:00`;

  const handleRequestStart = () => {
    setStartButtonsLocked(true);
    setErrorMessage('');
  };

  const handleRequestFailed = (message) => {
    setStartButtonsLocked(false);
    setErrorMessage(message || '開始処理に失敗しました。');
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          marginBottom: '8px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {isCompletedEvent && (
          <Alert severity="info" sx={{ width: '100%', maxWidth: 820 }}>
            ルームは終了済みのため、次のラウンドは開始できません。
          </Alert>
        )}
        <BetStartButton
          event={props.event}
          round={props.round}
          onStart={props.onStart}
          onRequestStart={handleRequestStart}
          onRequestFailed={handleRequestFailed}
          sharedDisabled={sharedDisabled}
        />
        <RoundFinishButton
          event={props.event}
          round={props.round}
          updateSaveRoundResultDialogVisible={props.updateSaveRoundResultDialogVisible}
          updateConfirmRoundResultDialogVisible={props.updateConfirmRoundResultDialogVisible}
          resetDisabledKey={props.resetDisabledKey}
        />
        <EventCheckOutButton
          event={props.event}
          round={props.round}
          dialogVisible={props.dialogVisible}
          updateDialogVisible={props.updateDialogVisible}
        />
      </Box>

      <Box
        sx={{
          width: '100%',
          maxWidth: 820,
          mx: 'auto',
          mb: 1,
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          backgroundColor: 'rgba(0, 0, 0, 0.02)'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, mb: 1 }}>
          <Typography>次ラウンド開始を時刻指定する</Typography>
          <Typography variant="body2">※1分以上未来を指定してください</Typography>
          <Typography variant="body2">※3日以上先を開始指定できません</Typography>
          <Typography variant="body2">※開始予約ボタンを押して時間までお待ちください</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 1.2, mb: 0.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">年</Typography>
            <FixedOrSelect
              value={selectedYear}
              options={yearOptions}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              minWidth={96}
              optionSuffix="年"
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">月</Typography>
            <FixedOrSelect
              value={selectedMonth}
              options={monthOptions}
              onChange={(event) => setSelectedMonth(Number(event.target.value))}
              optionSuffix="月"
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">日</Typography>
            <FixedOrSelect
              value={selectedDay}
              options={dayOptions}
              onChange={(event) => setSelectedDay(Number(event.target.value))}
              optionSuffix="日"
            />
          </Box>
          <FixedOrSelect
            value={selectedHour}
            options={hourOptions}
            onChange={(event) => setSelectedHour(Number(event.target.value))}
            optionSuffix="時"
          />
          <Typography sx={{ pb: 0.8 }}>：</Typography>
          <FixedOrSelect
            value={selectedMinute}
            options={minOptions}
            onChange={(event) => setSelectedMinute(Number(event.target.value))}
            optionSuffix="分"
          />

          <StartReserveButton
            event={props.event}
            round={props.round}
            scheduledStartTime={scheduledStartTime}
            disabled={sharedDisabled}
            onStart={props.onStart}
            onRequestStart={handleRequestStart}
            onRequestFailed={handleRequestFailed}
          />
        </Box>
      </Box>

      {errorMessage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Alert severity="error" sx={{ width: 'fit-content', maxWidth: '90%' }}>{errorMessage}</Alert>
        </Box>
      )}
    </>
  );
}

export default OwnerRoundControls;
